import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Truck, FileText, RefreshCcw, Shield, Phone, 
  Mail, MapPin, Clock, Building2, Scale,
  Package, AlertCircle, CreditCard, Users,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SELLER_AGREEMENT_URL = "https://docs.google.com/document/d/1zImfmb0f4lG_pQAbWPztwNO1x2JQHz7yqBP1jsH6W90/edit?usp=sharing";

const PolicyTerms = () => {
  const [activeTab, setActiveTab] = useState('shipping');

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-6" data-testid="terms-policies-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="terms-policies-title">Terms & Policies</h1>
          <p className="text-gray-600 mt-1">
            Important policies and terms governing your use of the Aniyor Vendor Platform
          </p>
        </div>

        {/* Main Seller Agreement Banner */}
        <Card className="mb-6 border-2 border-amber-300 bg-amber-50" data-testid="seller-agreement-banner">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-shrink-0 h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Scale className="h-6 w-6 text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-900">Master Seller Agreement</h3>
                <p className="text-sm text-amber-800 mt-1">
                  This is the primary legal document governing your relationship with Aniyor.
                  All operational policies on this page are extensions of this agreement. Please read it thoroughly.
                </p>
              </div>
              <Button
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                data-testid="view-seller-agreement-btn"
              >
                <a href={SELLER_AGREEMENT_URL} target="_blank" rel="noopener noreferrer">
                  <FileText className="h-4 w-4 mr-2" />
                  View Seller Agreement
                  <ExternalLink className="h-3 w-3 ml-2" />
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6" data-testid="policy-tabs">
          <TabsList className="grid grid-cols-5 gap-2 h-auto p-1 bg-gray-100 rounded-lg">
            <TabsTrigger 
              value="shipping" 
              className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              data-testid="tab-shipping"
            >
              <Truck className="h-4 w-4" />
              <span className="hidden sm:inline">Shipping Policy</span>
              <span className="sm:hidden">Shipping</span>
            </TabsTrigger>
            <TabsTrigger 
              value="terms" 
              className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              data-testid="tab-terms"
            >
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Terms & Conditions</span>
              <span className="sm:hidden">Terms</span>
            </TabsTrigger>
            <TabsTrigger 
              value="cancellation" 
              className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              data-testid="tab-cancellation"
            >
              <RefreshCcw className="h-4 w-4" />
              <span className="hidden sm:inline">Cancellations & Refunds</span>
              <span className="sm:hidden">Refunds</span>
            </TabsTrigger>
            <TabsTrigger 
              value="privacy" 
              className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              data-testid="tab-privacy"
            >
              <Shield className="h-4 w-4" />
              <span className="hidden sm:inline">Privacy Policy</span>
              <span className="sm:hidden">Privacy</span>
            </TabsTrigger>
            <TabsTrigger 
              value="contact" 
              className="flex items-center gap-2 py-3 px-4 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md"
              data-testid="tab-contact"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">Contact Us</span>
              <span className="sm:hidden">Contact</span>
            </TabsTrigger>
          </TabsList>

          {/* Shipping Policy Tab */}
          <TabsContent value="shipping" data-testid="content-shipping">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5 text-blue-600" />
                  Shipping & Fulfillment Policy (For Vendors)
                </CardTitle>
                <p className="text-sm text-gray-500">Last Updated: January 15, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-gray max-w-none">
                {/* Governance Note */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    GOVERNANCE NOTE
                  </h4>
                  <p className="text-sm text-amber-800 mb-2">
                    This policy is an operational extension of the master Seller Agreement. For comprehensive details regarding your legal relationship with Aniyor, liabilities, and dispute resolution mechanisms, please refer to the full Seller Agreement.
                  </p>
                  <p className="text-sm text-amber-800 mb-2">
                    All provisions in this policy should be viewed in the perspective of the Seller Agreement. In the event of any conflict between this policy and the Seller Agreement, the terms of the Seller Agreement shall prevail.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  This policy outlines the shipping and fulfillment standards required for all Vendors selling physical goods on the Aniyor Marketplace. By listing products, you agree to adhere to these service levels to ensure a consistent customer experience.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-3 flex items-center gap-2">
                  <Package className="h-5 w-5 text-blue-600" />
                  1. Shipping Partners & Integration
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Aniyor provides a centralized logistics network. Vendors are required to use the platform's integrated logistics partners (Shiprocket / Delhivery) for all physical shipments to ensure tracking visibility and insurance coverage.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Label Generation:</strong> Shipping labels must be generated directly from the Vendor Portal (Order Details {'>'} Pack & Ship).</li>
                  <li><strong>Self-Ship:</strong> Self-shipping (using your own couriers) is restricted to specific categories or Hyperlocal orders and requires prior approval.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  2. Service Level Agreement (SLA)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  To maintain the "Aniyor Promise" to customers, you must adhere to the following timelines:
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                  <p className="text-gray-700"><strong>Order Confirmation:</strong> Orders must be confirmed within 24 hours of receipt.</p>
                  <p className="text-gray-700"><strong>Ready to Ship:</strong> Products must be packed, labeled, and marked "Ready to Ship" within 48 hours (or the specific handling time defined in your listing).</p>
                  <p className="text-gray-700"><strong>Handover:</strong> Packages must be handed over to the courier partner during the scheduled pickup window. Frequent pickup failures may result in listing suppression.</p>
                </div>

                <h3 className="text-lg font-semibold mt-6 mb-3">3. Packaging Guidelines</h3>
                <p className="text-gray-700 leading-relaxed">
                  You are responsible for ensuring products are packed securely to prevent damage during transit.
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Lite Mode:</strong> For standard items, you may use standard packaging dimensions defined in the system (0.5kg/1kg flyers).</li>
                  <li><strong>Pro Mode:</strong> For fragile, heavy, or irregularly shaped items, you must define accurate custom dimensions. Volumetric weight discrepancies charged by the courier will be deducted from your payout.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">4. Hyperlocal & Serviceability</h3>
                <p className="text-gray-700 leading-relaxed mb-2">For Hyperlocal listings (Food, Grocery, Quick Commerce):</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>You must strictly define your Serviceable Radius and Pincodes in the Shipping Settings.</li>
                  <li>You are responsible for ensuring stock availability for immediate dispatch.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">5. Return Shipping</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Customer Returns:</strong> If a customer initiates a return for a damaged or incorrect product, the platform will arrange a reverse pickup.</li>
                  <li><strong>RTO (Return to Origin):</strong> If a package is undeliverable, it will be returned to your registered pickup address. RTO charges may apply depending on your Subscription Plan tier.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">6. Non-Physical Listings</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Services & Rentals:</strong> Fulfillment is verified via the "Booking Management" module. You must mark services as "Completed" or rentals as "Returned" to release payouts.</li>
                  <li><strong>Events:</strong> Tickets/Passes are fulfilled digitally via email/SMS immediately upon booking.</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Terms & Conditions Tab */}
          <TabsContent value="terms" data-testid="content-terms">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Vendor Terms and Conditions
                </CardTitle>
                <p className="text-sm text-gray-500">Last Updated: January 15, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-gray max-w-none">
                {/* Governance Note */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    GOVERNANCE NOTE
                  </h4>
                  <p className="text-sm text-amber-800 mb-2">
                    This document serves as the online Terms of Service for using the platform. It operates in conjunction with the master Seller Agreement. For comprehensive details regarding your legal relationship with Aniyor, liabilities, and dispute resolution mechanisms, please refer to the full Seller Agreement.
                  </p>
                  <p className="text-sm text-amber-800">
                    All provisions in these Terms should be viewed in the perspective of the Seller Agreement. In the event of any conflict between these Terms and the Seller Agreement, the terms of the Seller Agreement shall prevail.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  These Vendor Terms and Conditions ("Terms") constitute a legally binding agreement between you ("Vendor," "You," or "Your") and Aniyor ("We," "Us," or "Platform"). By registering as a vendor on seller.aniyor.net, you agree to be bound by these Terms.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  1. Vendor Account & Eligibility
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>1.1. Eligibility:</strong> You must be a registered business entity (Sole Proprietorship, LLP, Pvt Ltd) or an individual homepreneur capable of entering into contracts under Indian law.</li>
                  <li><strong>1.2. KYC Compliance:</strong> You agree to provide accurate documentation, including PAN, GSTIN (if applicable), and Bank Details. Aniyor reserves the right to suspend accounts with incomplete or unverifiable KYC.</li>
                  <li><strong>1.3. Account Security:</strong> You are responsible for safeguarding your login credentials, including passwords and 2FA OTPs.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3 flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-purple-600" />
                  2. Subscription Plans & Fees
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>2.1. Subscription Tiers:</strong> Aniyor operates on a subscription model (Basic, Pro, Business, Enterprise). Features, listing limits, and storage quotas vary by plan.</li>
                  <li><strong>2.2. Billing:</strong> Subscription fees are billed in advance (Monthly or Annually). Fees are non-refundable once the trial period expires.</li>
                  <li><strong>2.3. Commissions:</strong> In addition to the subscription fee, Aniyor charges a Success Fee (Commission) on every completed order. Commission rates (ranging from 8% to 15%) are determined by your Subscription Plan and Category.</li>
                  <li><strong>2.4. Add-Ons:</strong> Optional services (e.g., WhatsApp CRM, Extra Storage) are billed as per usage or flat monthly rates.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">3. Payouts & Settlements</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>3.1. Settlement Cycle:</strong> Payouts for completed orders are processed to your registered bank account after the standard settlement period (typically T+3 to T+7 days after delivery) to account for returns.</li>
                  <li><strong>3.2. Deductions:</strong> We reserve the right to deduct commissions, shipping charges, RTO penalties, and tax collected at source (TCS/TDS) before transferring the net amount.</li>
                  <li><strong>3.3. Penny Drop Verification:</strong> Bank accounts must be verified via a "penny drop" check before payouts can be enabled.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">4. Listing & Content Guidelines</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>4.1. Prohibited Items:</strong> You shall not list items that are illegal, counterfeit, stolen, or infringe upon intellectual property rights.</li>
                  <li><strong>4.2. Content License:</strong> By uploading images and descriptions to the Media Gallery, you grant Aniyor a non-exclusive, royalty-free license to use this content for marketing and platform display purposes.</li>
                  <li><strong>4.3. Accuracy:</strong> You warrant that all product descriptions, pricing, and inventory levels are accurate.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">5. Order Fulfillment</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>5.1.</strong> You agree to fulfill orders promptly in accordance with our Shipping Policy.</li>
                  <li><strong>5.2.</strong> Failure to ship orders within the SLA may result in order cancellation and penalties.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">6. Intellectual Property (Brand Registry)</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>6.1.</strong> Vendors owning trademarks may apply for Brand Registry.</li>
                  <li><strong>6.2.</strong> Aniyor respects IP rights and will act on takedown notices regarding listings that infringe on registered trademarks.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">7. Termination & Suspension</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>7.1. By Vendor:</strong> You may deactivate your account at any time via the Subscription Hub. Data retention policies apply.</li>
                  <li><strong>7.2. By Platform:</strong> We may suspend or terminate your account for violation of these Terms, selling counterfeit goods, or high return/dispute rates.</li>
                </ul>

                <h3 className="text-lg font-semibold mt-6 mb-3">8. Limitation of Liability</h3>
                <p className="text-gray-700 leading-relaxed">
                  The Platform is provided "as is." Aniyor is not liable for indirect damages, loss of business, or profits arising from your use of the marketplace.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-3">9. Governing Law</h3>
                <p className="text-gray-700 leading-relaxed">
                  These Terms shall be governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts in Mumbai, Maharashtra.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cancellations & Refunds Tab */}
          <TabsContent value="cancellation" data-testid="content-cancellation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCcw className="h-5 w-5 text-orange-600" />
                  Cancellations and Refunds Policy
                </CardTitle>
                <p className="text-sm text-gray-500">Last Updated: January 15, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-gray max-w-none">
                {/* Governance Note */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    GOVERNANCE NOTE
                  </h4>
                  <p className="text-sm text-amber-800 mb-2">
                    This policy is an operational extension of the master Seller Agreement. For comprehensive details regarding your legal relationship with Aniyor, liabilities, and dispute resolution mechanisms, please refer to the full Seller Agreement.
                  </p>
                  <p className="text-sm text-amber-800">
                    All provisions in this policy should be viewed in the perspective of the Seller Agreement. In the event of any conflict between this policy and the Seller Agreement, the terms of the Seller Agreement shall prevail.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed">
                  This policy covers two distinct areas: (A) Your subscription to the Aniyor Vendor Platform, and (B) How you, as a Vendor, must handle customer order cancellations and refunds.
                </p>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-6">
                  <h3 className="text-lg font-semibold mb-3 text-orange-800">
                    Part A: Vendor Subscription & Billing (Your Account)
                  </h3>
                </div>

                <h4 className="text-md font-semibold mt-4 mb-2">1. Subscription Cancellation</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Auto-Renewal:</strong> Your subscription (Basic, Pro, Business) renews automatically at the end of the billing cycle (Monthly/Annual) unless cancelled.</li>
                  <li><strong>How to Cancel:</strong> You can cancel your subscription at any time via Dashboard {'>'} Subscription Hub {'>'} Manage Plan.</li>
                  <li><strong>Effect of Cancellation:</strong> Upon cancellation, your store remains active until the end of the current paid period. After that, your account will revert to a "Dormant" state, and listings will be hidden.</li>
                </ul>

                <h4 className="text-md font-semibold mt-4 mb-2">2. Refund on Subscription Fees</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Free Trial:</strong> We offer a 30-day free trial for new vendors. You will not be charged if you cancel before the trial ends.</li>
                  <li><strong>No Refunds:</strong> Once a billing cycle (Monthly or Annual) has commenced and payment is processed, subscription fees are non-refundable, even if you do not use the platform during that period.</li>
                </ul>

                <h4 className="text-md font-semibold mt-4 mb-2">3. Ad Wallet & Add-ons</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Ad Credits:</strong> Prepaid credits in your Ad Wallet are non-refundable and non-transferable.</li>
                  <li><strong>Add-ons:</strong> Monthly add-ons (e.g., WhatsApp CRM) are billed pro-rata and are non-refundable.</li>
                </ul>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                  <h3 className="text-lg font-semibold mb-3 text-blue-800">
                    Part B: Marketplace Orders (Your Customers)
                  </h3>
                </div>

                <p className="text-gray-700 leading-relaxed mt-4">
                  As a vendor on Aniyor, you agree to adhere to the following standards regarding customer cancellations:
                </p>

                <h4 className="text-md font-semibold mt-4 mb-2">1. Customer Cancellations</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Before Shipping:</strong> Customers may cancel an order instantly before it is marked as "Ready to Ship" or "Shipped." You must accept these cancellations.</li>
                  <li><strong>After Shipping:</strong> Cancellation is usually not permitted after dispatch. The customer must accept delivery and initiate a return.</li>
                </ul>

                <h4 className="text-md font-semibold mt-4 mb-2">2. Returns & Refunds</h4>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Return Window:</strong> Aniyor enforces a standard 7-day return window for physical products (categories like Electronics or Fashion).</li>
                  <li><strong>Damaged/Defective:</strong> If a customer reports a damaged or defective item with proof (images/video), you must accept the return.</li>
                  <li><strong>Refund Processing:</strong> Once the returned item reaches your pickup location and passes your QC check (within 48 hours), Aniyor will process the refund to the customer.</li>
                  <li><strong>Services/Rentals:</strong> Refunds for services or rentals are governed by the specific cancellation terms defined in your listing (e.g., "Full refund if cancelled 24 hours prior").</li>
                </ul>

                <h4 className="text-md font-semibold mt-4 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  3. Disputes
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  In cases where the Vendor and Customer disagree (e.g., Vendor claims returned item is different), Aniyor Support will act as the final arbitrator based on the evidence provided.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Privacy Policy Tab */}
          <TabsContent value="privacy" data-testid="content-privacy">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-green-600" />
                  Privacy Policy
                </CardTitle>
                <p className="text-sm text-gray-500">Last Updated: January 15, 2026</p>
              </CardHeader>
              <CardContent className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  Your privacy is important to us. This Privacy Policy explains how Aniyor collects, uses, and protects your personal information when you use our Vendor Platform.
                </p>

                <p className="text-gray-700 leading-relaxed mt-4">
                  For the complete Privacy Policy document, please visit:
                </p>
                
                <a 
                  href="https://docs.google.com/document/d/1h5g90_NkoNJiqxdBe5LgxyElIka1cF61rHNNIWhg-Ko/edit?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium mt-2"
                >
                  <FileText className="h-4 w-4" />
                  View Full Privacy Policy Document
                </a>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
                  <h4 className="font-semibold text-green-800 mb-2">Key Points:</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>We collect information necessary to provide our services (KYC documents, business details, transaction data)</li>
                    <li>Your data is stored securely and never sold to third parties</li>
                    <li>You have the right to access, correct, or delete your personal data</li>
                    <li>We use industry-standard security measures to protect your information</li>
                    <li>Financial data is retained for 3 years for tax audit compliance</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Us Tab */}
          <TabsContent value="contact" data-testid="content-contact">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-indigo-600" />
                  Contact Us
                </CardTitle>
              </CardHeader>
              <CardContent className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed text-lg">
                  We are here to help you succeed on Aniyor. Whether you have questions about onboarding, managing your catalog, or technical support, our team is ready to assist.
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4">Get in Touch</h3>

                {/* Vendor Support */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-5 mb-4">
                  <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Vendor Support (Technical & Account)
                  </h4>
                  <p className="text-gray-700 mb-3">
                    For existing vendors, the fastest way to get help is via the Help & Support module in your dashboard.
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center gap-2">
                      <span className="font-medium">Ticket System:</span> 
                      Login to seller.aniyor.net {'>'} Help & Support {'>'} Create Ticket
                    </li>
                    <li className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-indigo-600" />
                      <span className="font-medium">Email:</span> 
                      <a href="mailto:support@aniyor.net" className="text-indigo-600 hover:underline">support@aniyor.net</a>
                    </li>
                    <li className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-indigo-600" />
                      <span className="font-medium">Response Time:</span> 
                      We aim to respond to all vendor inquiries within 24-48 hours.
                    </li>
                  </ul>
                </div>

                {/* Legal & Compliance */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-5 mb-4">
                  <h4 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <Scale className="h-5 w-5" />
                    Legal & Compliance
                  </h4>
                  <p className="text-gray-700 mb-2">
                    For queries related to Brand Registry, Intellectual Property, or Tax Compliance:
                  </p>
                  <p className="flex items-center gap-2 text-gray-700">
                    <Mail className="h-4 w-4 text-purple-600" />
                    <span className="font-medium">Email:</span> 
                    <a href="mailto:legal@aniyor.net" className="text-purple-600 hover:underline">legal@aniyor.net</a>
                  </p>
                </div>

                {/* Business Partnerships */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6">
                  <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    Business Partnerships & Enterprise
                  </h4>
                  <p className="text-gray-700 mb-2">
                    For Enterprise plan inquiries or strategic partnerships:
                  </p>
                  <p className="flex items-center gap-2 text-gray-700">
                    <Mail className="h-4 w-4 text-green-600" />
                    <span className="font-medium">Email:</span> 
                    <a href="mailto:partnerships@aniyor.net" className="text-green-600 hover:underline">partnerships@aniyor.net</a>
                  </p>
                </div>

                {/* Registered Office */}
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-gray-600" />
                    Registered Office
                  </h4>
                  <address className="not-italic text-gray-700 leading-relaxed">
                    <strong>Aniyor</strong><br />
                    Innov8 Times Square, Unit 2- 4th Flr,<br />
                    Times Square Bldg, Marol Naka,<br />
                    Andheri East, Mumbai 400059,<br />
                    Maharashtra, India.
                  </address>
                  <p className="flex items-center gap-2 text-gray-700 mt-3">
                    <Clock className="h-4 w-4 text-gray-600" />
                    <span className="font-medium">Operating Hours:</span> 
                    Monday to Saturday, 10:00 AM - 7:00 PM IST
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default PolicyTerms;
